/**
 * 消耗明细 (Admin) — 用户消耗列表、效率审计、CSV 导出
 */

import { authFetch } from './api.js';
import {
  setAdminUsageCurrentPage,
  getAdminUsageCache, setAdminUsageCache,
} from './state.js';
import { showToast, formatNumber, renderPagination } from './utils.js';
import { registerViewLoader } from './navigation.js';
import { getDashboardContext, getUserRole, setDashboardContext } from './state.js';
import { resolveDashboardContext, readDateInputs, applyDateInputs, getPeriodDateRange, formatRangeHint } from './dashboard-context.js';
import { readScopedQuery, writeScopedQuery } from './url-state.js';
import { withTableLoading } from './view-state.js';
import { formatBeijingDateKey } from '../time-beijing.js';
import { bindCostDetailPopovers, createUsageCostBadge } from './cost-detail-popover.js';

const ADMIN_USAGE_QUERY_SCHEMA = {
  page: { type: 'int', defaultValue: 1 },
  search: { type: 'string', defaultValue: '' },
  groupId: { type: 'string', defaultValue: '' },
  high: { type: 'string', defaultValue: '' },
};

let adminUsageGroupsLoaded = false;

function formatCurrency(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '¥0.00';
  return `¥${num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}

export function initAdminUsage() {
  hydrateAdminUsageFiltersFromUrl();
  registerViewLoader('admin-usage', () => {
    const ctx = getDashboardContext() || getPeriodDateRange('month');
    setDashboardContext(ctx);
    applyDateInputs('admin-usage-start', 'admin-usage-end', ctx);
    loadAdminUsageReport();
  });

  const filterBtn = document.getElementById('filter-usage-btn');
  if (filterBtn) filterBtn.addEventListener('click', () => loadAdminUsageReport(1));

  const exportBtn = document.getElementById('export-usage-btn');
  if (exportBtn) exportBtn.addEventListener('click', exportAdminReport);

  // 高消耗筛选复选框 — 前端筛选，无需重新请求
  const cb = document.getElementById('filter-high-consumption');
  if (cb) {
    cb.addEventListener('change', () => {
      const search = document.getElementById('search-admin-usage')?.value?.trim() || '';
      const groupId = document.getElementById('filter-admin-group')?.value || '';
      writeScopedQuery('adminusage', { page: 1, search, groupId, high: cb.checked ? '1' : '' });
      const cache = getAdminUsageCache();
      renderAdminUsageTable(cache.users, cache.systemTotal, cache.total, cache.page, cache.size);
    });
  }
}

async function ensureAdminUsageGroupFilter() {
  const select = document.getElementById('filter-admin-group');
  if (!select || adminUsageGroupsLoaded) return;
  adminUsageGroupsLoaded = true;
  const state = readScopedQuery('adminusage', ADMIN_USAGE_QUERY_SCHEMA);
  const isAdmin = getUserRole() === 'admin';
  select.replaceChildren(new Option(isAdmin ? '全部分组' : '当前分组', ''));
  if (!isAdmin) {
    select.disabled = true;
    return;
  }
  try {
    const response = await authFetch('/api/groups');
    if (!response.ok) return;
    const data = await response.json();
    (data.groups || []).forEach((group) => {
      const option = document.createElement('option');
      option.value = String(group.id);
      option.textContent = group.name || `分组 #${group.id}`;
      select.appendChild(option);
    });
    if (state.groupId) select.value = state.groupId;
  } catch {
    // 分组列表加载失败时保留“全部分组”兜底，不阻塞消耗明细。
  }
}

function hydrateAdminUsageFiltersFromUrl() {
  const state = readScopedQuery('adminusage', ADMIN_USAGE_QUERY_SCHEMA);
  const searchInput = document.getElementById('search-admin-usage');
  const groupSelect = document.getElementById('filter-admin-group');
  const highCheckbox = document.getElementById('filter-high-consumption');
  if (searchInput && state.search) searchInput.value = state.search;
  if (groupSelect) groupSelect.value = state.groupId || '';
  if (highCheckbox) highCheckbox.checked = state.high === '1';
}

export async function loadAdminUsageReport(page = null) {
  await ensureAdminUsageGroupFilter();
  const queryState = readScopedQuery('adminusage', ADMIN_USAGE_QUERY_SCHEMA);
  const targetPage = page == null ? queryState.page : page;
  setAdminUsageCurrentPage(targetPage);
  const search = document.getElementById('search-admin-usage')?.value?.trim() || '';
  let groupId = document.getElementById('filter-admin-group')?.value || '';
  const filter = window._usageFilter || '';
  const high = document.getElementById('filter-high-consumption')?.checked ? '1' : '';
  const tbody = document.getElementById('admin-usage-tbody');
  const data = await withTableLoading({
    tbody,
    colSpan: 10,
    page: targetPage,
    errorMessage: '加载消耗明细失败',
    onRetry: (p) => loadAdminUsageReport(p),
    onError: (error) => {
      console.error('Error loading admin usage:', error);
      showToast('加载消耗明细失败', 'error');
    },
    task: async () => {
    let ctx = getDashboardContext() || resolveDashboardContext();
    ctx = readDateInputs('admin-usage-start', 'admin-usage-end', ctx);
    setDashboardContext(ctx);
    if (!groupId && queryState.groupId === '' && ctx.groupId && getUserRole() === 'admin') {
      groupId = String(ctx.groupId);
      const groupSelect = document.getElementById('filter-admin-group');
      if (groupSelect) groupSelect.value = groupId;
    }
    writeScopedQuery('adminusage', { page: targetPage, search, groupId, high });
    const hint = document.getElementById('admin-usage-range-hint');
    if (hint) hint.textContent = '统计范围：' + formatRangeHint(ctx);

    const params = new URLSearchParams({
      page: targetPage, size: 15,
      startDate: ctx.startDate,
      endDate: ctx.endDate,
      ...(search && { search }),
      ...(groupId && { groupId }),
      ...(filter && { filter }),
    });

    const response = await authFetch(`/api/admin/usage?${params}`);
    if (!response.ok) throw new Error('Failed to load usage report');
      return response.json();
    },
  });
  if (!data) return;
  setAdminUsageCache(data);
  renderAdminUsageTable(data.users, data.systemTotal, data.total, targetPage, 15);
}

function renderAdminUsageTable(users, systemTotal, total, page, size) {
  const tbody = document.getElementById('admin-usage-tbody');
  const highConsumptionOnly = document.getElementById('filter-high-consumption')?.checked || false;

  let displayUsers = users;
  if (highConsumptionOnly) {
    displayUsers = users.filter(u => u.sellerspriteCalls > 10 && u.listingsCreated < 1);
  }

  if (!displayUsers || displayUsers.length === 0) {
    tbody.replaceChildren(createAdminUsageEmptyRow(highConsumptionOnly ? '没有符合条件的用户' : '暂无消耗数据'));
    renderPagination('admin-usage-pagination', 0, 1, size, (p) => loadAdminUsageReport(p));
    return;
  }

  const avgTokens = systemTotal?.avgTokensPerListing || 0;

  const rows = displayUsers.map((user) => createAdminUsageRow(user, avgTokens));
  if (systemTotal) rows.push(createAdminUsageTotalRow(systemTotal, avgTokens));
  tbody.replaceChildren(...rows);

  bindCostDetailPopovers(tbody);
  renderPagination('admin-usage-pagination', highConsumptionOnly ? displayUsers.length : total, page, size, (p) => loadAdminUsageReport(p));
}

function createAdminUsageCell(value = '', className = '') {
  const cell = document.createElement('td');
  if (className) cell.className = className;
  cell.textContent = String(value ?? '');
  return cell;
}

function createAdminUsageNumberCell(value, extraClass = '') {
  return createAdminUsageCell(formatNumber(value || 0), `admin-usage-number-cell ${extraClass}`.trim());
}

function usageEfficiencyClass(value, avgTokens) {
  if (value === null || avgTokens <= 0) return '';
  if (value > avgTokens * 2) return 'efficiency-danger';
  if (value > avgTokens * 1.5) return 'efficiency-warn';
  return '';
}

function createAdminUsageRow(user, avgTokens) {
  const row = document.createElement('tr');
  row.appendChild(createAdminUsageCell(user.name || 'N/A', 'admin-usage-name-cell'));
  row.appendChild(createAdminUsageNumberCell(user.listingsCreated, 'admin-usage-listings-cell'));
  row.appendChild(createAdminUsageCell(user.email || '', 'mono admin-usage-email-cell'));
  row.appendChild(createAdminUsageNumberCell(user.sellerspriteCalls));
  row.appendChild(createAdminUsageNumberCell(user.textTotalTokens ?? user.geminiTotalTokens));
  row.appendChild(createAdminUsageNumberCell(user.minimaxTotalTokens));
  row.appendChild(createAdminUsageNumberCell(user.imagesGenerated));
  row.appendChild(createAdminUsageNumberCell(user.videosGenerated));
  const cost = createAdminUsageCell('', 'admin-usage-cost-cell');
  cost.appendChild(createUsageCostBadge(user));
  row.appendChild(cost);
  const efficiency = createAdminUsageCell(user.tokensPerListing === null ? '未产出' : formatNumber(user.tokensPerListing), `admin-usage-efficiency-cell ${usageEfficiencyClass(user.tokensPerListing, avgTokens)}`.trim());
  row.appendChild(efficiency);
  return row;
}

function createAdminUsageTotalRow(systemTotal, avgTokens) {
  const row = document.createElement('tr');
  row.className = 'admin-usage-total-row';
  row.appendChild(createAdminUsageCell('系统总计', 'admin-usage-total-label'));
  row.appendChild(createAdminUsageNumberCell(systemTotal.totalListings));
  row.appendChild(createAdminUsageCell('', 'admin-usage-total-spacer'));
  row.appendChild(createAdminUsageNumberCell(systemTotal.totalSellerspriteCalls));
  row.appendChild(createAdminUsageNumberCell(systemTotal.totalTextTokens ?? systemTotal.totalGeminiTokens));
  row.appendChild(createAdminUsageNumberCell(systemTotal.totalMinimaxTokens));
  row.appendChild(createAdminUsageNumberCell(systemTotal.totalImageCount ?? systemTotal.totalImagesGenerated));
  row.appendChild(createAdminUsageNumberCell(systemTotal.totalVideoCount));
  const cost = createAdminUsageCell('', 'admin-usage-cost-cell');
  cost.appendChild(createUsageCostBadge(systemTotal));
  row.appendChild(cost);
  row.appendChild(createAdminUsageCell(`均值 ${formatNumber(avgTokens)}`, 'admin-usage-total-average'));
  return row;
}

function createAdminUsageEmptyRow(message) {
  const row = document.createElement('tr');
  const cell = document.createElement('td');
  cell.colSpan = 10;
  const empty = document.createElement('div');
  empty.className = 'empty-state';
  const icon = document.createElement('div');
  icon.className = 'empty-icon';
  icon.textContent = '💰';
  const text = document.createElement('p');
  text.textContent = message;
  empty.append(icon, text);
  cell.appendChild(empty);
  row.appendChild(cell);
  return row;
}

async function exportAdminReport() {
  try {
    const ctx = getDashboardContext() || getPeriodDateRange('month');
    const exportQs = new URLSearchParams({ startDate: ctx.startDate, endDate: ctx.endDate });
    const groupId = document.getElementById('filter-admin-group')?.value || '';
    if (groupId) exportQs.set('groupId', groupId);
    const response = await authFetch(`/api/admin/usage/export?${exportQs}`);
    if (!response.ok) throw new Error('导出失败');

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `usage_report_${formatBeijingDateKey()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    showToast('导出成功', 'success');
  } catch (error) {
    console.error('Error exporting report:', error);
    showToast('导出失败', 'error');
  }
}
